import * as React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import logo from "assets/logo.svg";
import CustomInput from "component/Input/CustomInput";
import PhoneNumberInputLogin from "component/Input/PhoneInputLogin";
import CustomButton from "component/Buttons/CustomButton";
import PrivacyPolicyText from "component/PrivacyPolicyText";
import { register } from 'api';
import { set_user } from 'redux/reducer/user';
import { useDispatch } from "react-redux";
import { set_message } from "redux/reducer/message";
import { persistVerificationUser } from 'utils/verificationGate';
import { useFormik } from 'formik';
import * as yup from 'yup';
import ReCAPTCHA from "react-google-recaptcha";
import Section from "../home/components/Section";
import {
  isDevTestCredentialsEnabled,
  isTestSignupEmail,
  TEST_OTP,
  TEST_SIGNUP,
} from 'constants/testCredentials';

const devInitialValues = isDevTestCredentialsEnabled
  ? { ...TEST_SIGNUP }
  : {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country_code: '+1',
  };

const validationSchema = yup.object({
  first_name: yup.string().required('Required').max(20, 'Max 20 Characters').matches(/^[^0-9]*$/, 'Numbers not allowed'),
  last_name: yup.string().required('Required').max(20, 'Max 20 Characters').matches(/^[^0-9]*$/, 'Numbers not allowed'),
  email: yup.string().required('Required').email('invalid email').max(50, 'maximum length is 50'),
  phone: yup.string().required('Required').max(10, 'Max 10 Digits').matches(/^[0-9]+$/, 'Numbers only'),
});

export const Register = () => {
  React.useEffect(() => {
    document.title = 'XGain | Register';
  }, []);

  const [loading, setLoading] = React.useState(false);
  const [captcha, setCaptcha] = React.useState(null);
  const [submitError, setSubmitError] = React.useState('');
  const recaptchaRef = React.useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');

  const resetCaptcha = React.useCallback(() => {
    setCaptcha(null);
    recaptchaRef.current?.reset();
  }, []);

  const formik = useFormik({
    initialValues: devInitialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setSubmitError('');
      if (values.email.includes('yopmail')) {
        return formik.setFieldError('email', 'Invalid Email');
      }
      const skipCaptcha = isTestSignupEmail(values.email);
      if (!skipCaptcha && !captcha) {
        setSubmitError('Please check "I\'m not a robot" before joining.');
        return;
      }
      setLoading(true);
      register({ ...values, captcha: captcha || 'dev-bypass' })
        .then((res) => {
          dispatch(set_user(res.data));
          persistVerificationUser(res.data);
          navigate('/verify/phone');
        })
        .catch((err) => {
          resetCaptcha();
          const data = err.response?.data;
          if (data?.phone) {
            formik.setFieldError('phone', data.phone);
          }
          if (data?.email) {
            formik.setFieldError('email', data.email);
          }
          let message = data?.msg
            || data?.errorMessage?.[0]
            || (typeof data?.err === 'string' ? data.err : null)
            || 'Unable to register. Please check your details and try again.';
          if (/captcha/i.test(message)) {
            message = 'Please check "I\'m not a robot" again, then tap Join Now.';
          }
          setSubmitError(message);
          dispatch(set_message(message));
        })
        .finally(() => setLoading(false));
    },
  });

  const handleInputChange = (e) => {
    const sanitizedValue = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    formik.setFieldValue(e.target.name, sanitizedValue);
  };

  return (
    <Section compact sx={{ bgcolor: '#21232E', pb: 4 }}>
      <Box
        component="form"
        className="login-form-dark"
        sx={{
          maxWidth: 400,
          width: '92%',
          color: '#fff',
        }}
        mx="auto"
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Box
          display="flex"
          sx={{
            height: '15vh',
            minHeight: '130px',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: { xs: '10%', md: '20%' },
          }}
        >
          <img src={logo} height="150px" alt="logo" />
        </Box>

        <Typography
          mt={3}
          sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }}
          variant="h6"
        >
          Sign Up to join Now
        </Typography>

        {isDevTestCredentialsEnabled && (
          <Typography sx={{ color: '#93c5fd', textAlign: 'center', mt: 1, mb: 1, fontSize: 13, lineHeight: 1.5 }}>
            Dev sign-up: {TEST_SIGNUP.email} · {TEST_SIGNUP.country_code} {TEST_SIGNUP.phone} · OTP {TEST_OTP}
            <br />
            (Use test@xgain.ai only for login — that account already exists.)
          </Typography>
        )}

        <Box mt={2}>
          {submitError && (
            <Typography sx={{ color: '#f87171', textAlign: 'center', mb: 1, fontSize: 14 }}>
              {submitError}
            </Typography>
          )}
          <CustomInput
            dark
            name="first_name"
            placeHolder="First name"
            value={formik.values.first_name}
            onChange={handleInputChange}
            error={formik.touched.first_name && Boolean(formik.errors.first_name)}
            onBlur={formik.handleBlur}
            helperText={formik.touched.first_name && formik.errors.first_name}
          />
          <CustomInput
            dark
            name="last_name"
            placeHolder="Last name"
            value={formik.values.last_name}
            onChange={handleInputChange}
            error={formik.touched.last_name && Boolean(formik.errors.last_name)}
            onBlur={formik.handleBlur}
            helperText={formik.touched.last_name && formik.errors.last_name}
          />
          <CustomInput
            dark
            name="email"
            placeHolder="Email address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <PhoneNumberInputLogin
            dark
            id="phone"
            name="phone"
            data={formik.values}
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            formik={formik}
          />
        </Box>

        <Box
          className={`login-recaptcha-wrap ${isMobile ? 'login-recaptcha-wrap--compact' : 'login-recaptcha-wrap--normal'}`}
          sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
        >
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Ld8rWknAAAAAD_jifcFUozxmQK79tSd4J4V9C2p"
            theme="dark"
            size={isMobile ? 'compact' : 'normal'}
            onChange={(token) => {
              setCaptcha(token);
              setSubmitError('');
            }}
            onExpired={resetCaptcha}
          />
        </Box>

        {(captcha || isTestSignupEmail(formik.values.email)) && (
          <CustomButton loading={loading} type="submit" text="Join Now" />
        )}

        <Box my={3} textAlign="center">
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            Already joined?
            <Link to="/login" style={{ marginLeft: 4, color: '#60a5fa', textDecoration: 'none' }}>
              Login
            </Link>
          </Typography>
        </Box>

        <Box sx={{ color: 'rgba(255,255,255,0.75)', '& a': { color: '#60a5fa' } }}>
          <PrivacyPolicyText />
        </Box>
      </Box>
    </Section>
  );
};

export default Register;
